import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';

import IconWithTooltip from '@/components/IconWithTooltip';
import { useAlertDialog } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DataTableAsync } from '@/components/ui/data-table';
import { Form } from '@/components/ui/form';
import { FormItem } from '@/components/ui/form-item';
import { Input } from '@/components/ui/input';
import { usePagination } from '@/hooks/use-pagination';
import PermissionData from '@/types/Permission/PermissionData';
import { ColumnDef } from '@tanstack/react-table';

import { SFilterContainer } from '../User/styles';
import { useDeletePermissionMutation } from './api/use-delete-permission.mutation';
import { useGetPermissionsQuery } from './api/use-get-permissions.query';

interface PermissionsTableProps {
  onEdit: (record: PermissionData) => void;
  setTableRefetch: (refetch: () => void) => void;
}

export const PermissionsTable = ({ onEdit, setTableRefetch }: PermissionsTableProps) => {
  const { dispatchPagination, paginationConfig } = usePagination();
  const { t } = useTranslation();
  const filterForm = useForm({
    defaultValues: {
      search: '',
    },
  });
  const { alert } = useAlertDialog();

  const [filters, setFilters] = useState({});

  const getPermissionsQuery = useGetPermissionsQuery({
    ...paginationConfig,
    ...filters,
    dispatchPagination,
  });
  const deletePermissionMutation = useDeletePermissionMutation();

  useEffect(() => {
    if (getPermissionsQuery?.refetch) {
      setTableRefetch(getPermissionsQuery.refetch);
    }
  }, [getPermissionsQuery?.refetch]);

  const columns = useMemo(
    (): ColumnDef<PermissionData>[] => [
      {
        header: t('pages.admin.permission.table.header.name'),
        accessorKey: 'name',
        cell: ({ row }) => row.original.name,
      },
      {
        id: 'actions',
        enablePinning: true,
        accessorKey: 'action',
        header: t('actions'),
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div>
              <IconWithTooltip
                aria-label={t('edit')}
                action="edit"
                title={t('edit')}
                permission="admin.permission.update"
                onClick={() => onEdit(record)}
              />

              <IconWithTooltip
                aria-label={t('remove')}
                action="destroy"
                title={t('remove')}
                permission="admin.permission.destroy"
                onClick={() => {
                  alert({
                    type: 'confirm',
                    title: t('pages.admin.permission.delete.title'),
                    subTitle: t('pages.admin.permission.delete.subtitle'),
                    cancelText: t('pages.admin.permission.delete.cancel'),
                    okText: t('pages.admin.permission.delete.confirm'),
                    onOk: async () => {
                      await deletePermissionMutation.mutateAsync(record);
                      getPermissionsQuery?.refetch();
                    },
                  });
                }}
              />
            </div>
          );
        },
      },
    ],
    [getPermissionsQuery?.data],
  );

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-hidden">
      <SFilterContainer>
        <Form
          form={filterForm}
          className="flex w-full flex-row items-end gap-2"
          onSubmit={values => setFilters(values)}
        >
          <div className="w-full">
            <FormItem name="search" className="w-full" label={t('pages.admin.permission.search')}>
              <Input className="h-8 w-full" />
            </FormItem>
          </div>
          <div className="filter-button">
            <FormItem label="" name="search" className="form-item-without-label">
              <Button variant="default" className="uppercase" type="submit">
                <FaSearch /> {t('form.actions.search')}
              </Button>
            </FormItem>
          </div>
          <div className="filter-clear">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setFilters({});
                filterForm.reset();
              }}
            >
              {t('pages.admin.permission.buttonClearFilter')}
            </Button>
          </div>
        </Form>
      </SFilterContainer>
      <DataTableAsync
        dispatchPagination={dispatchPagination}
        paginationConfig={paginationConfig}
        columns={columns}
        data={getPermissionsQuery?.data?.data ?? []}
        withPagination
        isLoading={getPermissionsQuery?.isLoading}
        isFetching={getPermissionsQuery?.isFetching}
        pageCount={getPermissionsQuery?.data?.last_page ?? 0}
      />
    </div>
  );
};
